/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { type Map } from 'mapbox-gl';

import {
    MapControlButton,
    MapExpandButton,
    MapLoadingIndicator,
    MapSatelliteToggle,
    MapZoomControls,
    useMap,
} from 'shared-ui/map';

import styles from './SearchMapControls.module.scss';

// GA tracking ids preserved from the legacy local control composite so existing
// analytics dashboards keep resolving these interactions.
const SATELLITE_GA_TRACKING_ID = '828990255377';
const EXPAND_GA_TRACKING_ID = '866374897906';
const SEARCH_HERE_GA_TRACKING_ID = '101599300840';

interface SearchMapControlsProps {
    /**
     * Whether the map is presented full-screen. Drives the expand button's
     * icon/label. On mobile the map is always full-screen, so the caller passes
     * `true` and the button doubles as the modal-close control.
     */
    isExpanded: boolean;
    /** Toggles the expanded/full-screen state (and closes the mobile modal). */
    onExpandClick: () => void;
    /** Runs a new search constrained to the current map viewport. */
    onSearchClick: () => void;
    /** Whether search results are currently being fetched. */
    isFetching?: boolean;
}

/**
 * Search-map control overlay, composed from the shared atomic map controls and
 * arranged over the canvas. Rendered after the canvas in the DOM so keyboard tab
 * order reaches the map before its controls. The overlay itself is transparent
 * to pointer events (so map drags pass through); each shared control re-enables
 * pointer events on itself.
 *
 * Layout: satellite toggle (top-left), a centered slot showing the shared
 * loading indicator while fetching else the "Search Here" pill, and a top-right
 * stack of the expand button then the shared zoom controls (which provide the
 * accessibility settings modal and keyboard pan controls).
 */
export function SearchMapControls({
    isExpanded,
    onExpandClick,
    onSearchClick,
    isFetching,
}: SearchMapControlsProps) {
    const { mapInstance } = useMap();

    // Nothing to control until the map exists (matches the legacy composite).
    if (!mapInstance) return null;

    return (
        <div className={styles.controls}>
            <div className={styles.leftSlot}>
                <MapSatelliteToggle gaTrackingId={SATELLITE_GA_TRACKING_ID} />
            </div>

            <div className={styles.centerSlot}>
                {isFetching ? (
                    <MapLoadingIndicator />
                ) : (
                    <SearchHereControl
                        mapInstance={mapInstance}
                        onSearchClick={onSearchClick}
                    />
                )}
            </div>

            <div className={styles.rightSlot}>
                <MapExpandButton
                    isExpanded={isExpanded}
                    onToggle={onExpandClick}
                    gaTrackingId={EXPAND_GA_TRACKING_ID}
                    gaTrackingValue={isExpanded ? 'Show List' : 'Expanded'}
                />
                <MapZoomControls />
            </div>
        </div>
    );
}

interface SearchHereControlProps {
    mapInstance: Map;
    onSearchClick: () => void;
}

/**
 * "Search Here" pill that appears only after the user has moved the map, letting
 * them re-run the search over the current viewport. The move-detection logic is
 * preserved from the legacy control: a `moveend` triggered by a real user
 * gesture (its `originalEvent` targets the map, not `window`) reveals the pill.
 */
function SearchHereControl({ mapInstance, onSearchClick }: SearchHereControlProps) {
    const [hasMapMoved, setHasMapMoved] = React.useState(false);

    React.useEffect(() => {
        // Guard against a torn-down map (cleanup / HMR races).
        if (isMapRemoved(mapInstance)) return;

        const handleMoveEnd = (event: { originalEvent?: Event }) => {
            // Ignore programmatic moves (no originalEvent) and window-driven
            // resizes; only reveal the pill for genuine map-gesture moves.
            if (event.originalEvent && event.originalEvent.target !== window) {
                setHasMapMoved(true);
            }
        };

        mapInstance.on('moveend', handleMoveEnd);

        return () => {
            mapInstance.off('moveend', handleMoveEnd);
        };
    }, [mapInstance]);

    const handleSearchHereClick = () => {
        setHasMapMoved(false);
        onSearchClick();
    };

    if (!hasMapMoved) return null;

    return (
        <MapControlButton
            shape="pill"
            onClick={handleSearchHereClick}
            gaTrackingId={SEARCH_HERE_GA_TRACKING_ID}
        >
            Search Here
        </MapControlButton>
    );
}

/**
 * `_removed` is an internal Mapbox flag marking a torn-down map. Reading it
 * guards against operating on a stale instance during cleanup / HMR.
 */
function isMapRemoved(map: Map): boolean {
    return (map as { _removed?: boolean })._removed === true;
}

// cSpell:ignore moveend
